import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { Trash2, Plus, GripVertical, LayoutGrid, ShieldCheck, CalendarCheck, Headphones, Star, Clock, MapPin, CreditCard, Users, Heart, Award, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons
const AVAILABLE_ICONS = [
  { name: 'shield-check', icon: ShieldCheck, label: 'Shield Check' },
  { name: 'calendar-check', icon: CalendarCheck, label: 'Calendar Check' },
  { name: 'headphones', icon: Headphones, label: 'Headphones' },
  { name: 'star', icon: Star, label: 'Star' },
  { name: 'clock', icon: Clock, label: 'Clock' },
  { name: 'map-pin', icon: MapPin, label: 'Map Pin' },
  { name: 'credit-card', icon: CreditCard, label: 'Credit Card' },
  { name: 'users', icon: Users, label: 'Users' },
  { name: 'heart', icon: Heart, label: 'Heart' },
  { name: 'award', icon: Award, label: 'Award' },
  { name: 'zap', icon: Zap, label: 'Zap' },
];

const getIconComponent = (iconName) => {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found ? found.icon : Star;
};

const InfoCardsManager = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/info-cards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If no cards, use defaults
      if (response.data.length === 0) {
        const publicResponse = await axios.get(`${API}/info-cards`);
        setCards(publicResponse.data);
      } else {
        setCards(response.data);
      }
    } catch (error) {
      console.error('Error fetching info cards:', error);
      // Load defaults on error
      setCards([
        {
          id: '1',
          icon: 'shield-check',
          title_hr: 'Sigurna rezervacija',
          title_en: 'Secure Booking',
          title_de: 'Sichere Buchung',
          title_it: 'Prenotazione Sicura',
          description_hr: 'Vaši podaci su zaštićeni enkripcijom najviše razine.',
          description_en: 'Your data is protected with the highest level of encryption.',
          description_de: 'Ihre Daten sind mit höchster Verschlüsselung geschützt.',
          description_it: 'I tuoi dati sono protetti con la massima crittografia.',
          order: 0
        },
        {
          id: '2',
          icon: 'calendar-check',
          title_hr: 'Trenutna potvrda',
          title_en: 'Instant Confirmation',
          title_de: 'Sofortige Bestätigung',
          title_it: 'Conferma Istantanea',
          description_hr: 'Primite potvrdu rezervacije odmah nakon uplate.',
          description_en: 'Receive booking confirmation immediately after payment.',
          description_de: 'Erhalten Sie die Buchungsbestätigung sofort nach der Zahlung.',
          description_it: 'Ricevi la conferma della prenotazione subito dopo il pagamento.',
          order: 1
        },
        {
          id: '3',
          icon: 'headphones',
          title_hr: 'Podrška 24/7',
          title_en: '24/7 Support',
          title_de: '24/7 Unterstützung',
          title_it: 'Supporto 24/7',
          description_hr: 'Naš tim je uvijek dostupan za sva vaša pitanja.',
          description_en: 'Our team is always available for all your questions.',
          description_de: 'Unser Team ist immer für alle Ihre Fragen erreichbar.',
          description_it: 'Il nostro team è sempre disponibile per tutte le tue domande.',
          order: 2
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCards();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/info-cards`, cards, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Info cards saved successfully');
    } catch (error) {
      toast.error('Failed to save info cards');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCard = () => {
    if (cards.length >= 6) {
      toast.error('Maximum 6 cards allowed');
      return;
    }
    
    setCards([
      ...cards,
      {
        id: Date.now().toString(),
        icon: 'star',
        title_hr: '',
        title_en: '',
        title_de: '',
        title_it: '',
        description_hr: '',
        description_en: '',
        description_de: '',
        description_it: '',
        order: cards.length
      }
    ]);
  };

  const handleRemoveCard = (id) => {
    if (cards.length <= 1) {
      toast.error('At least one card is required');
      return;
    }
    setCards(cards.filter(c => c.id !== id));
  };

  const handleUpdateCard = (id, field, value) => {
    setCards(cards.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const moveCard = (index, direction) => {
    const newCards = [...cards];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= cards.length) return;
    
    [newCards[index], newCards[newIndex]] = [newCards[newIndex], newCards[index]];
    setCards(newCards);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5" />
            Homepage Info Cards / Kartice na Početnoj
          </div>
          <Button onClick={handleAddCard} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {cards.map((card, index) => {
          const IconComponent = getIconComponent(card.icon);
          
          return (
            <div key={card.id} className="border rounded-lg p-4 bg-stone-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-5 h-5 text-stone-400" />
                  <span className="font-medium">Card {index + 1}</span>
                  <div className="flex gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveCard(index, -1)}
                      disabled={index === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveCard(index, 1)}
                      disabled={index === cards.length - 1}
                    >
                      ↓
                    </Button>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveCard(card.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Icon selector */}
              <div className="mb-4">
                <Label className="mb-2 block">Icon</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                    <button
                      key={name}
                      onClick={() => {
                        handleUpdateCard(card.id, 'icon', name);
                        handleUpdateCard(card.id, 'custom_icon_url', ''); // Clear custom icon when selecting preset
                      }}
                      className={`p-2 rounded-lg border transition-all ${
                        card.icon === name && !card.custom_icon_url
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                      title={label}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
                {/* Custom Icon URL */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-stone-500">Ili dodaj svoju ikonu (URL)</Label>
                    <Input
                      value={card.custom_icon_url || ''}
                      onChange={(e) => handleUpdateCard(card.id, 'custom_icon_url', e.target.value)}
                      placeholder="https://example.com/icon.svg"
                      className="mt-1"
                    />
                  </div>
                  {card.custom_icon_url && (
                    <div className="w-10 h-10 border rounded-lg flex items-center justify-center bg-stone-100">
                      <img 
                        src={card.custom_icon_url} 
                        alt="Custom icon" 
                        className="w-6 h-6 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Titles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div>
                  <Label className="text-xs">Title (HR)</Label>
                  <Input
                    value={card.title_hr}
                    onChange={(e) => handleUpdateCard(card.id, 'title_hr', e.target.value)}
                    placeholder="Croatian title"
                  />
                </div>
                <div>
                  <Label className="text-xs">Title (EN)</Label>
                  <Input
                    value={card.title_en}
                    onChange={(e) => handleUpdateCard(card.id, 'title_en', e.target.value)}
                    placeholder="English title"
                  />
                </div>
                <div>
                  <Label className="text-xs">Title (DE)</Label>
                  <Input
                    value={card.title_de}
                    onChange={(e) => handleUpdateCard(card.id, 'title_de', e.target.value)}
                    placeholder="German title"
                  />
                </div>
                <div>
                  <Label className="text-xs">Title (IT)</Label>
                  <Input
                    value={card.title_it}
                    onChange={(e) => handleUpdateCard(card.id, 'title_it', e.target.value)}
                    placeholder="Italian title"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Description (HR)</Label>
                  <textarea
                    value={card.description_hr}
                    onChange={(e) => handleUpdateCard(card.id, 'description_hr', e.target.value)}
                    placeholder="Croatian description"
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none h-20"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description (EN)</Label>
                  <textarea
                    value={card.description_en}
                    onChange={(e) => handleUpdateCard(card.id, 'description_en', e.target.value)}
                    placeholder="English description"
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none h-20"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description (DE)</Label>
                  <textarea
                    value={card.description_de}
                    onChange={(e) => handleUpdateCard(card.id, 'description_de', e.target.value)}
                    placeholder="German description"
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none h-20"
                  />
                </div>
                <div>
                  <Label className="text-xs">Description (IT)</Label>
                  <textarea
                    value={card.description_it}
                    onChange={(e) => handleUpdateCard(card.id, 'description_it', e.target.value)}
                    placeholder="Italian description"
                    className="w-full px-3 py-2 text-sm border rounded-md resize-none h-20"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-4 p-4 bg-white rounded-lg border">
                <p className="text-xs text-stone-500 mb-2">Preview:</p>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-5 h-5 text-stone-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm">{card.title_en || 'Title'}</h4>
                    <p className="text-xs text-stone-600">{card.description_en || 'Description text...'}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? 'Saving...' : 'Save All Cards'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default InfoCardsManager;
